import { useQuery } from '@apollo/client'
import {
  BIG_ITEMS_PER_PAGE,
  INITIAL_PAGE_INDEX,
  SMALL_ITEMS_PER_PAGE,
} from 'constants/pagination'
import { useItemsPerPage } from 'hooks/useItemsPerPage'
import { usePagination } from 'hooks/usePagination'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { MarketRelation } from 'types/indexer'
import {
  FilterOptions,
  LatestListQuery,
  MarketplaceController,
} from '../types/Marketplace.type'
import { GET_LATEST_LIST } from '../utils/Marketplace.graphql'

const getQueryVariables = (option: string) => {
  switch (option) {
    case FilterOptions.NEWEST:
      return {
        orderBy: { createdAt: 'desc' },
      }
    case FilterOptions.OLDEST:
      return {
        orderBy: { createdAt: 'asc' },
      }
    case FilterOptions.CHEAPEST:
      return {
        orderBy: { list: { price: 'asc' } },
      }
    case FilterOptions.EXPENSIVE:
      return {
        orderBy: { list: { price: 'desc' } },
      }
    default:
      return {
        orderBy: { createdAt: 'desc' },
      }
  }
}

const useLatestListingsController = (props: MarketplaceController) => {
  const {
    initialPageIndex = INITIAL_PAGE_INDEX,
    initialSSRData = null,
    filter,
  } = props

  const [page, setCurrentPage] = useState(initialPageIndex)
  const [totalItems, setTotalItems] = useState(0)
  const [data, setData] = useState<MarketRelation[]>(initialSSRData)
  const [isInitialRender, setIsInitialRender] = useState(true)

  const { itemsPerPage } = useItemsPerPage({
    smallScreenItems: SMALL_ITEMS_PER_PAGE,
    bigScreenItems: BIG_ITEMS_PER_PAGE,
  })

  const router = useRouter()

  const { loading, error } = useQuery<LatestListQuery>(GET_LATEST_LIST, {
    variables: {
      limit: itemsPerPage,
      offset: page * itemsPerPage,
      ...getQueryVariables(filter),
    },
    onCompleted: (_data) => {
      setTotalItems(_data.markets_aggregate.aggregate.count)
      setData(_data.markets)
    },
  })

  const { setPage } = usePagination({
    totalItems: totalItems,
    initialPageSize: itemsPerPage,
    initialPage: INITIAL_PAGE_INDEX,
  })

  const handleFilterChange = () => {
    if (isInitialRender) {
      setIsInitialRender(false)
    } else {
      setPage(0)
    }
    router.push(
      `/market?tab=listings&orderby=${filter}&page=${initialPageIndex}`
    )
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  useEffect(() => {
    handleFilterChange()
  }, [filter])

  const handlePageChange = () => {
    if (loading) return
    window.scrollTo({ top: 0, behavior: 'smooth' })
    setPage(page)
    router.push(`/market?tab=listings&orderby=${filter}&page=${page}`)
  }

  useEffect(() => {
    handlePageChange()
  }, [page])

  return {
    data,
    loading: (loading || !data) && !initialSSRData,
    error: error,
    page,
    itemsPerPage,
    totalItems,
    setCurrentPage,
  }
}

export { useLatestListingsController, getQueryVariables }
